<template>
  <div
    class="mt-4 mb-6 grid grid-cols-15 items-center gap-x-4 gap-y-2 rounded-md"
  >
    <div class="col-span-15 flex gap-x-4 lg:col-span-4">
      <div class="grow">
        <label for="search" class="sr-only text-sm font-medium text-gray-700">{{
          t('search')
        }}</label>
        <div class="flex rounded-md shadow-xs">
          <div class="relative grow self-center focus-within:z-10">
            <div
              class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3"
            >
              <MagnifyingGlassIcon
                class="h-5 w-5 text-gray-400"
                aria-hidden="true"
              />
            </div>
            <input
              id="search"
              ref="searchInput"
              v-model="jobAlertListStore.filter.email"
              type="search"
              name="search"
              class="block w-full rounded-md border-gray-300 pl-10 text-sm focus:border-indigo-500 focus:ring-indigo-500"
              :placeholder="t('jobAlerts.search.email')"
            />
            <div
              v-if="!jobAlertListStore.filter.email"
              class="absolute inset-y-0 right-0 hidden py-1.5 pr-1.5 sm:block"
            >
              <kbd
                class="inline-flex items-center rounded-sm border border-gray-200 px-2 font-sans text-sm font-medium text-gray-400"
              >
                Ctrl /
              </kbd>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="col-span-15 lg:col-span-4">
      <div class="flex grow flex-col gap-x-2 lg:flex-row lg:items-center">
        <label for="sites" class="text-sm font-medium text-gray-700">{{
          t('jobAlerts.search.site')
        }}</label>

        <Multiselect
          id="sites"
          ref="multiselect"
          v-model="site"
          :options="searchSite"
          :filterResults="false"
          :noOptionsText="t('vacancy.company.noOptions')"
          :placeholder="t('jobAlerts.search.sitePlaceholder')"
          :resolveOnLoad="true"
          :minChars="0"
          :delay="500"
          :searchable="true"
          :object="true"
          valueProp="id"
          label="name"
          trackBy="name"
          inputType="search"
          autocomplete="off"
          class="mt-1 block w-full rounded-md border-gray-300 shadow-xs focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
        />
      </div>
    </div>

    <div class="col-span-15 lg:col-span-4">
      <div class="flex grow flex-col gap-x-2 lg:flex-row lg:items-center">
        <label for="company" class="block text-sm font-medium text-gray-700">{{
          t('jobAlerts.search.company')
        }}</label>
        <Multiselect
          id="companyModelValue"
          v-model="company"
          class="mt-1 block w-full rounded-md border-gray-300 shadow-xs focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          :placeholder="t('vacancy.company.placeholder')"
          :minChars="0"
          :filterResults="false"
          :resolveOnLoad="true"
          :delay="500"
          :searchable="true"
          :options="searchCompany"
          :object="true"
          :noOptionsText="t('vacancy.company.noOptions')"
          valueProp="id"
          label="name"
          trackBy="name"
          inputType="search"
          autocomplete="off"
        />
      </div>
    </div>

    <div class="col-span-15 ml-1 lg:col-span-3">
      <Listbox
        v-model="jobAlertListStore.filter.frequency"
        as="div"
        class="flex flex-col gap-x-2 lg:flex-row lg:items-center"
      >
        <ListboxLabel class="block shrink-0 text-sm font-medium text-gray-700">
          {{ t('jobAlerts.search.frequency') }}
        </ListboxLabel>
        <div class="relative md:w-full">
          <ListboxButton
            class="relative w-full cursor-default rounded-md border border-gray-300 bg-white py-1.5 pr-10 pl-3 text-left shadow-xs focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 focus:outline-hidden sm:text-sm"
          >
            <span class="ml-2 block truncate capitalize">{{
              jobAlertListStore.filter.frequency?.toLowerCase() ||
              t('candidates.filters.all')
            }}</span>
            <span
              class="pointer-events-none absolute inset-y-0 right-0 ml-2 flex items-center pr-2"
            >
              <ChevronUpDownIcon
                class="h-5 w-5 text-gray-400"
                aria-hidden="true"
              />
            </span>
          </ListboxButton>

          <transition
            leaveActiveClass="transition ease-in duration-100"
            leaveFromClass="opacity-100"
            leaveToClass="opacity-0"
          >
            <ListboxOptions
              class="absolute z-10 mt-1 max-h-56 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-hidden sm:text-sm"
            >
              <ListboxOption
                v-for="(type, index) in frequencies"
                :key="index"
                v-slot="{ active, selected }"
                as="template"
                :value="type"
              >
                <li
                  :class="[
                    active ? 'bg-indigo-600 text-white' : 'text-gray-900',
                    'relative cursor-default py-2 pr-9 pl-3 select-none',
                  ]"
                >
                  <span
                    :class="[
                      selected ? 'font-semibold' : 'font-normal',
                      'block truncate capitalize',
                    ]"
                  >
                    {{ type?.toLowerCase() || t('candidates.filters.all') }}
                  </span>
                </li>
              </ListboxOption>
            </ListboxOptions>
          </transition>
        </div>
      </Listbox>
    </div>

    <div class="col-span-15 ml-1 lg:col-span-3">
      <Listbox
        v-model="jobAlertListStore.filter.state"
        as="div"
        class="flex flex-col gap-x-2 lg:flex-row lg:items-center"
      >
        <ListboxLabel class="block shrink-0 text-sm font-medium text-gray-700">
          {{ t('jobAlerts.search.status') }}
        </ListboxLabel>
        <div class="relative md:w-full">
          <ListboxButton
            class="relative w-full cursor-default rounded-md border border-gray-300 bg-white py-1.5 pr-10 pl-3 text-left shadow-xs focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 focus:outline-hidden sm:text-sm"
          >
            <span class="ml-2 block truncate capitalize">{{
              jobAlertListStore.filter.state?.toLowerCase() ||
              t('candidates.filters.all')
            }}</span>
            <span
              class="pointer-events-none absolute inset-y-0 right-0 ml-2 flex items-center pr-2"
            >
              <ChevronUpDownIcon
                class="h-5 w-5 text-gray-400"
                aria-hidden="true"
              />
            </span>
          </ListboxButton>

          <transition
            leaveActiveClass="transition ease-in duration-100"
            leaveFromClass="opacity-100"
            leaveToClass="opacity-0"
          >
            <ListboxOptions
              class="absolute z-10 mt-1 max-h-56 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-hidden sm:text-sm"
            >
              <ListboxOption
                v-for="(type, index) in states"
                :key="index"
                v-slot="{ active, selected }"
                as="template"
                :value="type"
              >
                <li
                  :class="[
                    active ? 'bg-indigo-600 text-white' : 'text-gray-900',
                    'relative cursor-default py-2 pr-9 pl-3 select-none',
                  ]"
                >
                  <span
                    :class="[
                      selected ? 'font-semibold' : 'font-normal',
                      'block truncate capitalize',
                    ]"
                  >
                    {{ type?.toLowerCase() || t('candidates.filters.all') }}
                  </span>
                </li>
              </ListboxOption>
            </ListboxOptions>
          </transition>
        </div>
      </Listbox>
    </div>

    <div class="col-span-15 flex items-center lg:col-span-5">
      <Combobox v-model="selectedLocation" nullable name="location">
        <ComboboxLabel
          class="mr-2 block shrink-0 text-sm font-medium text-gray-700"
          >{{ t('vacancies.search.location') }}</ComboboxLabel
        >
        <div class="relative w-full grow">
          <ComboboxInput
            class="relative w-full cursor-default rounded-l-md border border-gray-300 bg-white py-2 pr-10 pl-3 text-left shadow-xs focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 focus:outline-hidden sm:text-sm"
            :displayValue="(location) => locationLabel(location)"
            :placeholder="t('vacancies.search.locationPlaceholder')"
            @change="locationQuery = $event.target.value"
          />
          <ComboboxButton
            class="absolute inset-y-0 right-0 flex items-center pr-2"
          >
            <ChevronUpDownIcon
              class="h-5 w-5 text-gray-400"
              aria-hidden="true"
            />
          </ComboboxButton>
          <TransitionRoot
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            @after-leave="locationQuery = ''"
          >
            <ComboboxOptions
              v-show="locationQuery !== ''"
              class="absolute z-1 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg focus:outline-2 sm:text-sm"
            >
              <div
                v-if="filteredLocations.length === 0 && locationQuery !== ''"
                class="relative cursor-default px-4 py-2 text-gray-700 select-none"
              >
                {{ t('vacancies.search.noLocations') }}
              </div>

              <ComboboxOption
                v-for="location in filteredLocations"
                :key="location.place_id"
                v-slot="{ selected, active }"
                as="template"
                :value="location"
              >
                <li
                  class="location-option relative cursor-default py-2 pr-4 pl-10 text-gray-900 select-none"
                  :class="active ? `active` : 'inactive'"
                >
                  <span
                    class="block truncate"
                    :class="{
                      'font-medium': selected,
                      'font-normal': !selected,
                    }"
                  >
                    {{ locationLabel(location) }}
                  </span>
                  <span
                    v-if="selected"
                    class="selected absolute inset-y-0 left-0 flex items-center pl-3"
                  >
                    <CheckIcon class="h-5 w-5" aria-hidden="true" />
                  </span>
                </li>
              </ComboboxOption>
            </ComboboxOptions>
          </TransitionRoot>
        </div>
      </Combobox>

      <Listbox
        v-model="jobAlertListStore.filter.searchAreas[0].radiusKm"
        as="div"
        class="flex flex-col gap-x-2 lg:flex-row lg:items-center"
        @update:model-value="
          jobAlertListStore.filter.searchAreas[0].lat && loadJobAlerts()
        "
      >
        <ListboxLabel
          class="sr-only block shrink-0 text-sm font-medium text-gray-700"
        >
          {{ t('vacancies.search.distance') }}
        </ListboxLabel>
        <div class="relative md:w-28">
          <ListboxButton
            class="relative w-full cursor-default rounded-r-md border border-gray-300 bg-white py-2 pr-10 pl-3 text-left shadow-xs focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 focus:outline-hidden sm:text-sm"
          >
            <span class="ml-2 block truncate capitalize"
              >+{{ jobAlertListStore.filter.searchAreas[0].radiusKm }}km</span
            >
            <span
              class="pointer-events-none absolute inset-y-0 right-0 ml-2 flex items-center pr-2"
            >
              <ChevronUpDownIcon
                class="h-5 w-5 text-gray-400"
                aria-hidden="true"
              />
            </span>
          </ListboxButton>

          <transition
            leaveActiveClass="transition ease-in duration-100"
            leaveFromClass="opacity-100"
            leaveToClass="opacity-0"
          >
            <ListboxOptions
              class="absolute z-10 mt-1 max-h-56 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-hidden sm:text-sm"
            >
              <ListboxOption
                v-for="distance in [1, 5, 10, 20, 30, 50]"
                :key="distance"
                v-slot="{ active, selected }"
                as="template"
                :value="distance"
              >
                <li
                  :class="[
                    active ? 'bg-indigo-600 text-white' : 'text-gray-900',
                    'relative cursor-default py-2 pr-9 pl-3 select-none',
                  ]"
                >
                  <span
                    :class="[
                      selected ? 'font-semibold' : 'font-normal',
                      'block truncate capitalize',
                    ]"
                  >
                    +{{ distance }}km
                  </span>
                </li>
              </ListboxOption>
            </ListboxOptions>
          </transition>
        </div>
      </Listbox>
    </div>

    <div
      class="col-span-15 flex flex-col items-start gap-x-2 lg:col-span-3 lg:flex-row lg:items-center"
    >
      <label
        for="createdAfter"
        class="block shrink-0 text-sm font-medium text-gray-700"
        >{{ t('jobAlerts.search.createdAfter') }}</label
      >

      <DatePicker
        :value="jobAlertListStore.filter.createdAfter"
        :inputAttr="{ id: 'createdAfter' }"
        type="dateTime"
        format="D MMM YYYY"
        inputClass="focus:border-indigo-500 border-gray-300 focus:border-indigo-500 focus:ring-indigo-500 w-full block rounded-md shadow-xs focus:outline-hidden sm:text-sm"
        :clearable="true"
        :editable="false"
        class="mt-1"
        @change="(value) => updateDateRange('createdAfter', value)"
      />
    </div>
    <div
      class="col-span-15 flex flex-col items-start gap-x-2 lg:col-span-3 lg:flex-row lg:items-center"
    >
      <label
        for="createdBefore"
        class="block shrink-0 text-sm font-medium text-gray-700"
        >{{ t('jobAlerts.search.createdBefore') }}</label
      >

      <DatePicker
        :value="jobAlertListStore.filter.createdBefore"
        :inputAttr="{ id: 'createdBefore' }"
        type="dateTime"
        format="D MMM YYYY"
        inputClass="focus:border-indigo-500 border-gray-300 focus:border-indigo-500 focus:ring-indigo-500 w-full block rounded-md shadow-xs focus:outline-hidden sm:text-sm"
        :clearable="true"
        :editable="false"
        class="mt-1"
        @change="(value) => updateDateRange('createdBefore', value)"
      />
    </div>
    <div class="col-span-1 mt-1 ml-auto">
      <button
        type="button"
        class="inline-flex items-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-xs hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 focus:outline-hidden disabled:bg-indigo-400 sm:w-auto"
        :title="
          !jobAlertListStore.filter.siteId
            ? t('jobAlerts.search.searchTooltip')
            : null
        "
        :disabled="
          !jobAlertListStore.filter.siteId || jobAlertListStore.isLoading
        "
        @click="showExtraParams = !showExtraParams"
      >
        {{
          showExtraParams
            ? t('jobAlerts.search.hideAdvancedSearch')
            : t('jobAlerts.search.advancedSearch')
        }}
        <LoadingSpinner
          v-if="jobAlertListStore.isLoading"
          class="ml-2 h-4 w-4"
        />
      </button>
    </div>

    <template v-if="showExtraParams">
      <div class="col-span-15 my-2 border border-dashed border-gray-300"></div>

      <span
        v-if="!Object.keys(siteTagGroups).length"
        class="col-span-15 text-sm text-gray-700"
        >There are no extra params available for this site</span
      >

      <div
        v-for="(group, key) in siteTagGroups"
        :key="key"
        class="col-span-15 lg:col-span-5"
      >
        <div class="flex grow flex-col gap-x-2 lg:flex-row lg:items-center">
          <label :for="key" class="text-sm font-medium text-gray-700">
            {{ t(`jobAlerts.search.${key}`) }}
          </label>
          <Multiselect
            :id="key"
            :maxHeight="400"
            :modelValue="selectedTags[key]"
            :options="group"
            :searchable="true"
            :object="true"
            valueProp="key"
            label="label"
            trackBy="label"
            class="mt-1 block w-full rounded-md border-gray-300 shadow-xs focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
            mode="tags"
            @change="(e) => updateTags(e, key)"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<script setup>
import { onBeforeUnmount, ref, watch } from 'vue'
import {
  CheckIcon,
  ChevronUpDownIcon,
  MagnifyingGlassIcon,
} from '@heroicons/vue/20/solid'
import { useJobAlertListStore } from '../../store/jobAlertListStore'
import Multiselect from '@vueform/multiselect'
import tooManagement from '../../api/tooManagement'
import { useI18n } from 'vue-i18n'
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxLabel,
  ComboboxOption,
  ComboboxOptions,
  Listbox,
  ListboxButton,
  ListboxLabel,
  ListboxOption,
  ListboxOptions,
  TransitionRoot,
} from '@headlessui/vue'
import DatePicker from 'vue-datepicker-next'
import { watchDebounced } from '@vueuse/core'
import { endOfDay, startOfDay } from 'date-fns'
import LoadingSpinner from '../LoadingSpinner.vue'
import { locationIQAPIKey } from '../../utils/location'

const frequencies = [null, 'DAILY', 'WEEKLY']
const states = [null, 'ACTIVE', 'INACTIVE']

const jobAlertListStore = useJobAlertListStore()
const { t } = useI18n({
  inheritLocale: true,
  useScope: 'global',
})

const site = ref({})
if (jobAlertListStore.filter.siteId) {
  tooManagement.getSite(jobAlertListStore.filter.siteId).then((response) => {
    site.value = response.data
    fetchSiteTags(site.value).then(() => {
      let existingTags = false
      for (const prop in jobAlertListStore.filter) {
        let groupCategory
        switch (prop) {
          case 'education':
            groupCategory = 'VACANCY_EDUCATION'
            break
          case 'discipline':
            groupCategory = 'VACANCY_WORKFIELD'
            break
          case 'workCategory':
            groupCategory = 'VACANCY_WORKCATEGORY'
            break
          case 'workExperience':
            groupCategory = 'VACANCY_WORKEXPERIENCE'
            break
          case 'employmentType':
            groupCategory = 'VACANCY_EMPLOYMENTTYPE'
            break
          case 'region':
            groupCategory = 'REGION'
            break
        }
        if (groupCategory) {
          selectedTags.value[groupCategory] = siteTagGroups.value[
            groupCategory
          ].filter((tag) => jobAlertListStore.filter[prop].includes(tag.key))
          if (selectedTags.value[groupCategory].length) {
            existingTags = true
          }
        }
      }
      if (existingTags) {
        showExtraParams.value = true
      }
    })
  })
}

const siteTagGroups = ref({})

const company = ref({})
if (jobAlertListStore.filter.companyIds?.length) {
  tooManagement
    .getCompany(jobAlertListStore.filter.companyIds[0])
    .then((response) => {
      company.value = response.data
    })
}

const selectedLocation = ref(jobAlertListStore.filter.searchAreas[0].label)
const locationQuery = ref('')
const filteredLocations = ref([])

function locationLabel(location) {
  if (location?.display_place) {
    return `${location?.display_place || location}${location?.type === 'state' ? ' (Provincie)' : ''}`
  }
  return location || ''
}

const selectedTags = ref({
  VACANCY_EDUCATION: [],
  VACANCY_WORKFIELD: [],
  VACANCY_WORKCATEGORY: [],
  VACANCY_WORKEXPERIENCE: [],
  VACANCY_EMPLOYMENTTYPE: [],
  REGION: [],
})
const showExtraParams = ref(false)

const searchSite = async (filter = '') => {
  const queryRes = await tooManagement.getSites({
    filter: filter || '',
    size: 15,
  })
  return queryRes.data
}

const searchCompany = async (filter = '') => {
  const queryRes = await tooManagement.getCompanies({
    filter: filter || '',
    size: 15,
  })
  return queryRes.data
}

const loadJobAlerts = async () => {
  jobAlertListStore.page.number = 1
  await jobAlertListStore.load()
}

const updateDateRange = (fieldName, value) => {
  if (fieldName === 'createdBefore' && value) {
    jobAlertListStore.filter[fieldName] = endOfDay(value)
  } else if (fieldName === 'createdAfter' && value) {
    jobAlertListStore.filter[fieldName] = startOfDay(value)
  } else {
    jobAlertListStore.filter[fieldName] = null
  }

  loadJobAlerts()
}
const updateTags = (tags, group) => {
  selectedTags.value[group] = tags

  let tagField
  switch (group) {
    case 'VACANCY_EDUCATION':
      tagField = 'education'
      break
    case 'VACANCY_WORKFIELD':
      tagField = 'discipline'
      break
    case 'VACANCY_WORKCATEGORY':
      tagField = 'workCategory'
      break
    case 'VACANCY_WORKEXPERIENCE':
      tagField = 'workExperience'
      break
    case 'VACANCY_EMPLOYMENTTYPE':
      tagField = 'employmentType'
      break
    case 'REGION':
      tagField = 'region'
      break
  }
  jobAlertListStore.filter[tagField] = tags.map((tag) => tag.key)
  loadJobAlerts()
}

const fetchSiteTags = async (site) => {
  const [tagGroupsResponse, regionsResponse] = await Promise.all([
    tooManagement.getTagGroups(site.id),
    tooManagement.getSiteRegions(site.id),
  ])
  const tagGroupPromises = tagGroupsResponse.data.map((tagGroup) =>
    tooManagement.getTags(tagGroup.id),
  )
  if (regionsResponse.data?.length) {
    siteTagGroups.value['REGION'] = regionsResponse.data.map((region) => ({
      key: region.slug,
      label: region.name,
    }))
  }

  const tags = await Promise.all(tagGroupPromises)
  tags.forEach((tagGroup, index) => {
    if (tagGroup.data.length) {
      siteTagGroups.value[tagGroupsResponse.data[index].tagCategory] =
        tagGroup.data
    }
  })
}

watchDebounced(
  () => jobAlertListStore.filter.email,
  () => {
    loadJobAlerts()
  },
  { debounce: 500 },
)

watch(site, async (newSite) => {
  jobAlertListStore.filter.siteId = newSite?.id || null
  loadJobAlerts()

  if (newSite?.id) {
    fetchSiteTags(newSite)
  } else {
    showExtraParams.value = false
    siteTagGroups.value = {}
    selectedTags.value = {
      VACANCY_EDUCATION: [],
      VACANCY_WORKFIELD: [],
      VACANCY_WORKCATEGORY: [],
      VACANCY_WORKEXPERIENCE: [],
      VACANCY_EMPLOYMENTTYPE: [],
      REGION: [],
    }
  }
})

watch(company, (newCompany) => {
  jobAlertListStore.filter.companyIds = newCompany?.id ? [newCompany.id] : []
  loadJobAlerts()
})

watch(
  () => jobAlertListStore.filter.frequency,
  () => {
    loadJobAlerts()
  },
)

watch(
  () => jobAlertListStore.filter.state,
  () => {
    loadJobAlerts()
  },
)

watch(selectedLocation, (newLocation) => {
  if (newLocation?.lat) {
    jobAlertListStore.filter.searchAreas[0].lat = parseFloat(newLocation.lat)
    jobAlertListStore.filter.searchAreas[0].lng = parseFloat(newLocation.lon)
    jobAlertListStore.filter.searchAreas[0].label = newLocation.display_place
  } else {
    jobAlertListStore.filter.searchAreas[0].lat = null
    jobAlertListStore.filter.searchAreas[0].lng = null
    jobAlertListStore.filter.searchAreas[0].label = ''
  }

  loadJobAlerts()
})

watchDebounced(
  locationQuery,
  async () => {
    filteredLocations.value = []
    if (locationQuery.value) {
      const response = await fetch(
        `https://api.locationiq.com/v1/autocomplete?q=${locationQuery.value}&key=${locationIQAPIKey}&&countrycodes=nl&accept-language=nl&tag=place:city,place:town,place:borough,place:municipality,place:village,place:hamlet,place:state`,
      )
      if (!response.ok) {
        return
      }
      const data = await response.json()
      if (data && data.length) {
        filteredLocations.value = data
      }
    }
  },
  { debounce: 500, maxWait: 5000 },
)

const searchInput = ref(null)
const onKeydown = (event) => {
  if (event.key === '/' && event.ctrlKey) {
    searchInput.value?.focus()
  }
}

window.addEventListener('keydown', onKeydown)
onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKeydown)
})

loadJobAlerts()
</script>
